/* Place-kind metadata: the one table mapping a server kind string onto its
   icon, colour and display order.
 *
 * Kinds come from the model (app/pipeline/analyze.py) as free text that happens
 * to be one of four values. Everything that renders a venue — pin, pill, list
 * row, chip, card — needs the same icon and the same hue for the same kind, and
 * they live in five different shadow roots. One table, imported.
 *
 * `unknown` is not an error path. A model that returns a fifth value must still
 * render, so an unmapped kind falls back to the generic pin in the Place hue.
 * Same for `off`, which is not a kind at all but the state of a venue we could
 * not geocode — it borrows the shape of one so callers can treat them alike.
 */

export interface KindMeta {
  /** g-icon name. */
  icon: string;
  /** The CSS custom property carrying this kind's hue, from tokens.ts. */
  color: string;
}

/** Canonical order, mirroring analyze.py's enum — the chip order falls out of it. */
export const PLACE_KINDS = ['Food', 'Stay', 'Place to Visit', 'Shop'] as const;

/**
 * Chip label for a kind.
 *
 * Only one kind needs shortening, and it needs it badly: "Place to Visit" plus
 * its count is 130px of a 380px panel, which pushed two of the five chips off
 * the scrollable row where nobody would find them. "Visit" reads the same in a
 * row whose other members are Food, Stay and Shop.
 *
 * A display concern only — the stored value stays the server's.
 */
export const kindLabel = (kind: string): string =>
  kind === 'Place to Visit' ? 'Visit' : kind;

const KIND_META: Record<string, KindMeta> = {
  Food: { icon: 'utensils', color: 'var(--k-food)' },
  Stay: { icon: 'bed', color: 'var(--k-stay)' },
  'Place to Visit': { icon: 'camera', color: 'var(--k-place)' },
  Shop: { icon: 'bag', color: 'var(--k-shop)' },
};

const OFF_MAP: KindMeta = { icon: 'ban', color: 'var(--k-off)' };
const UNKNOWN: KindMeta = { icon: 'pin', color: 'var(--k-place)' };

/**
 * Icon + hue for a venue.
 *
 * Takes `geocoded` because presentation depends on it: a venue with no
 * coordinates reads as grey and struck-through wherever it appears, regardless
 * of what kind it is.
 */
export function kindMeta(kind: string, geocoded = true): KindMeta {
  if (!geocoded) return OFF_MAP;
  return KIND_META[kind] ?? UNKNOWN;
}

/* `localityOf` MOVED to packages/core in Phase 4 of the native client — it is
   pure string work over two payload fields, so both clients share one
   implementation rather than two that drift.

   Re-exported here rather than repointed at the call sites: `place-row`,
   `place-detail` and `venue-card` all reach for it beside `kindMeta`, and this
   is the module that means "what a venue looks like". Keeping the name in
   place is one edit instead of three. */
export { localityOf } from '../../../../packages/core/index.js';
